/**
 * useBlockquote Hook
 * 
 * A custom hook to build blockquote toggle functionality with full control
 * over rendering and behavior for Tiptap editors.
 */

import React, { useMemo, useCallback, useEffect } from 'react';
import { Editor } from '@tiptap/react';

// Icon component for blockquote
export const BlockQuoteIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
        <rect x="4" y="4" width="3" height="16" fill="currentColor" stroke="none" rx="1" />
        <line x1="11" y1="8" x2="20" y2="8" strokeWidth="2" />
        <line x1="11" y1="16" x2="18" y2="16" strokeWidth="2" />
    </svg>
);

export interface UseBlockquoteOptions {
    /** The Tiptap editor instance */
    editor: Editor | null;
    /** Hides the button if blockquote cannot be applied */
    hideWhenUnavailable?: boolean;
    /** Callback fired after toggling blockquote */
    onToggled?: () => void;
    /** Custom label for the blockquote button */
    label?: string;
}

export interface UseBlockquoteReturn {
    /** Whether the button should be rendered */
    isVisible: boolean;
    /** If the blockquote is currently active */
    isActive: boolean;
    /** If the blockquote toggle is currently allowed */
    canToggle: boolean;
    /** Function to toggle blockquote formatting */
    handleToggle: () => boolean;
    /** Accessible label text for the button */
    label: string;
    /** Keyboard shortcut (Cmd/Ctrl + Shift + B) */
    shortcutKeys: string[];
    /** Icon component for the blockquote button */
    Icon: React.FC<{ className?: string }>;
}

/**
 * Checks if blockquote can be toggled in the current editor state.
 * @param editor - The Tiptap editor instance
 * @param turnInto - If true, check if can turn into blockquote (not toggle)
 * @returns Whether the blockquote can be toggled
 */
export function canToggleBlockquote(editor: Editor | null, turnInto?: boolean): boolean {
    if (!editor) return false;

    // Check if blockquote extension is available
    if (!editor.extensionManager.extensions.find(ext => ext.name === 'blockquote')) {
        return false;
    }

    // If turnInto is true, check if we can wrap in blockquote (not already in one)
    if (turnInto) {
        return !editor.isActive('blockquote') && editor.can().toggleBlockquote();
    }

    return editor.can().toggleBlockquote();
}

/**
 * Programmatically toggles blockquote formatting for the current selection.
 * @param editor - The Tiptap editor instance
 * @returns Whether the toggle was successful
 */
export function toggleBlockquote(editor: Editor | null): boolean {
    if (!editor || !canToggleBlockquote(editor)) {
        return false;
    }

    editor.chain().focus().toggleBlockquote().run();
    return true;
}

/**
 * Custom hook to build your own blockquote toggle button with full control
 * over rendering and behavior.
 */
export function useBlockquote(options: UseBlockquoteOptions): UseBlockquoteReturn {
    const { editor, hideWhenUnavailable = false, onToggled, label: customLabel = 'Quote' } = options;

    const isActive = useMemo(() => {
        return editor?.isActive('blockquote') ?? false;
    }, [editor, editor?.isActive('blockquote')]);

    const canToggle = useMemo(() => {
        return canToggleBlockquote(editor);
    }, [editor, editor?.state.selection]);

    const isVisible = useMemo(() => {
        if (hideWhenUnavailable && !canToggle) {
            return false;
        }
        return true;
    }, [hideWhenUnavailable, canToggle]);

    const handleToggle = useCallback(() => {
        const success = toggleBlockquote(editor);
        if (success && onToggled) {
            onToggled();
        }
        return success;
    }, [editor, onToggled]);

    const shortcutKeys = useMemo(() => {
        const isMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0;
        return isMac ? ['⌘', 'Shift', 'B'] : ['Ctrl', 'Shift', 'B'];
    }, []);

    // Register keyboard shortcut using native event listener
    useEffect(() => {
        if (!editor || !canToggle) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            // Check for Cmd/Ctrl + Shift + B
            const isMod = e.metaKey || e.ctrlKey;
            if (isMod && e.shiftKey && e.key.toLowerCase() === 'b') {
                e.preventDefault();
                handleToggle();
            }
        };

        // Add listener to the editor's dom element if available, otherwise document
        const targetElement = editor.view?.dom?.closest('.tiptap-editor-content') || document;
        targetElement.addEventListener('keydown', handleKeyDown as EventListener);

        return () => {
            targetElement.removeEventListener('keydown', handleKeyDown as EventListener);
        };
    }, [editor, canToggle, handleToggle]);

    return {
        isVisible,
        isActive,
        canToggle,
        handleToggle,
        label: customLabel,
        shortcutKeys,
        Icon: BlockQuoteIcon,
    };
}

export default useBlockquote;
